import { useTranslate } from '../../application/i18n/LocaleProvider';
import { cardOf } from '../../domain/game/overtakeDeck';
import OvertakeCard from './OvertakeCard';
import { OvertakeBackStack } from './OvertakeCardBack';

/**
 * The table: the stock, the card to beat, and what the rival is holding
 * (SPEC-011 PR-11.5, D-29).
 *
 * THREE COLUMNS, LAID OUT AS THE TABLE ACTUALLY IS. The stock is a pile on your
 * left, the round is in the middle, and the rival's hand is a pile on the right
 * — each named and counted the same way, because three piles that describe
 * themselves in three different shapes are three separate things to learn. The
 * stock sat off to the right of a sentence before this, which is the one place
 * it is neither part of the round nor part of anybody's hand.
 *
 * The rival's count is drawn TWICE on purpose, here and on the fan above: the
 * fan is what the rival is holding and this is the pile it is drawn from, and a
 * reader looking at the table for what is left should not have to look up.
 *
 * THE STANDING CARD IS THE ONE THAT MATTERS, and it is the thing every hand is
 * measured against. The cards it climbed over shingle out behind it, showing
 * their left edge — the origin plate and the marque — so the round reads as the
 * sentence it is: a 246 answered by a 290 and then by a 324.
 *
 * They are FACES and not backs. A back means hidden, and none of these is: every
 * one was played face up in front of the reader, and drawing them face-down
 * would take back information they already have. What they give up is their
 * figures, which nobody has to beat any more.
 *
 * THE DIRECTION IS NOT DECORATION. A counter-call inverts the comparison
 * (D-7), and a plate still reading "least weight" after somebody turned it over
 * would be the screen lying about the rule currently in force. So the caption
 * carries the direction as well as the figure, in text, in the order it is
 * spoken — and the same is true of the two flags: a doubled round changes the
 * payout and puts a card at risk, and neither is derivable from anything else on
 * screen.
 */
export default function OvertakeTable({ match, deck, mySide = 'player', rivalCards = 0 }) {
    const t = useTranslate();
    const played = match.pile ?? [];
    const standing = played[played.length - 1] ?? null;
    // Everything the standing card climbed over, oldest first.
    const climbed = played.slice(0, -1);

    return (
        <section className="overtake-table" aria-label={t('play.table')}>
            <OvertakeBackStack count={(match.stock ?? []).length} label={t('play.table.stock')} />

            <div className="overtake-table__middle">
                {/* Who played the card that is standing. Its own line above the
                    pile rather than a chip inside it, so the pile below is
                    exactly the cards — which is what lets the climb be drawn
                    behind the standing card at the same size as it. */}
                <span className="overtake-table__by">
                    {standing ? t(standing.by === mySide ? 'play.you' : 'play.rival') : ''}
                </span>

                {standing ? (
                    <ol className="overtake-table__pile">
                        <li className="overtake-table__played">
                            {climbed.length > 0 ? (
                                <span className="overtake-table__history" aria-hidden="true">
                                    {climbed.map((entry) => (
                                        <OvertakeCard
                                            key={entry.id}
                                            car={cardOf(deck, entry.id)}
                                            spec={match.spec}
                                            size="climbed"
                                        />
                                    ))}
                                </span>
                            ) : null}

                            <OvertakeCard
                                car={cardOf(deck, standing.id)}
                                spec={match.spec}
                                size="table"
                                isStanding
                            />
                        </li>
                    </ol>
                ) : (
                    <p className="overtake-table__empty">{t('play.table.empty')}</p>
                )}

                {/* WHAT IS BEING PLAYED FOR, under the card it is being played
                    on. The direction is not decoration: a counter-call inverts
                    the comparison (D-7), and a plate still reading "least
                    weight" after somebody turned it over would be the screen
                    lying about the rule in force. With nothing called yet the
                    same line names the column, so the middle of the table is
                    captioned in both states rather than only in one. */}
                <p className="overtake-table__caption">
                    {/* THE COLUMN'S OWN NAME, like the two piles beside it — the
                        three captions are one row and have to read as one. What
                        is being played FOR is on the scoreboard, where a reader
                        checks it without looking down (D-29); repeating it here
                        made the middle of the table the only column labelled
                        with something other than what it is. */}
                    <span className={`overtake-table__called${match.spec ? ' is-live' : ''}`}>
                        <span className="overtake-table__mark" aria-hidden="true">▲</span>
                        <span className="overtake-table__figure">{t('play.table.caption')}</span>
                    </span>

                    {/* The two flags ride the caption rather than taking a row
                        of their own: a doubled round changes the payout and puts
                        a card at risk, and a turned-over one inverts the
                        comparison — both belong with the figure they qualify. */}
                    {match.doubled ? (
                        <span className="overtake-table__flag">{t('play.doubled')}</span>
                    ) : null}
                    {match.countered ? (
                        <span className="overtake-table__flag">{t('play.countered')}</span>
                    ) : null}
                </p>
            </div>

            <OvertakeBackStack count={rivalCards} label={t('play.table.rivalHand')} />
        </section>
    );
}
